import {useState, useMemo, useCallback, useEffect} from 'react';
import {getActivityHistory, searchPhoneNumber} from '@/services/activities';
import {useDataTable} from './useDataTable-hook';
import {downloadCSVFiles} from '@/helpers/shadcn/utils';
import {buildQueryParams} from '@/utils/commons';
import {logger} from '@/helpers/logger';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = {column: 'createdAt', direction: 'asc'};
const DATE_FORMAT_OPTIONS = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
};

export const useActivityHistory = (showToast) => {
  const [searchBy, setSearchBy] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tableData, setTableData] = useState([]);
  const [showStores, setShowStores] = useState(false);
  const [storesList, setStoresList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const {setPageIndex, setSortingObj, pageIndex, sortingObj} = useDataTable();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Unknown Date';

    return new Date(dateString)
      .toLocaleString('en-GB', DATE_FORMAT_OPTIONS)
      .replace(
        /(\d{2})\/(\d{2})\/(\d{2}), (\d{2}):(\d{2}):(\d{2})/,
        '$3-$2-$1 $4:$5:$6'
      );
  }, []);

  const trimOrNull = useCallback(
    (value) => (value?.trim() ? value.trim() : null),
    []
  );

  const getActivityName = useCallback(
    (item) => {
      return (
        trimOrNull(item.activityName) ||
        trimOrNull(item.translations?.activityName?.en) ||
        trimOrNull(item.name) ||
        (item.slug
          ? item.slug
              .split('::')[1]
              ?.replaceAll('-', ' ')
              .replaceAll(/\b\w/g, (l) => l.toUpperCase()) || 'Unknown Activity'
          : 'Unknown Activity')
      );
    },
    [trimOrNull]
  );

  const getDescription = useCallback(
    (item, activityName) => {
      const description =
        trimOrNull(item.description) ||
        trimOrNull(item.translations?.activityDescription?.en);

      if (description) {
        return description;
      }

      switch (item.type) {
        case 'redemption':
          return `Points redeemed - ${Math.abs(item.pointsAwarded || 0)} points used`;
        case 'earning':
          return `Points earned - ${item.pointsAwarded || 0} points added`;
        default:
          return `Activity: ${activityName}`;
      }
    },
    [trimOrNull]
  );

  const transformApiData = useCallback(
    (apiData) => {
      if (!apiData || !Array.isArray(apiData)) return [];

      return apiData.map((item) => {
        const activityName = getActivityName(item);
        const description = getDescription(item, activityName);

        return {
          id: item?.id,
          activityName: activityName,
          pointsAwarded: item?.pointsAwarded || 0,
          endpointBalance: item?.endpointBalance || 0,
          type: item?.pointsType || 'Unknown',
          description,
          createdAt: item?.createdAt
        };
      });
    },
    [getActivityName, getDescription]
  );

  const getQueryParams = useCallback(
    (storeId, includePagination = true) => {
      return buildQueryParams({
        storeId,
        ...(sortingObj?.column && {
          sortBy: sortingObj.column,
          order: sortingObj.direction
        }),
        ...(includePagination && {
          pageSize: DEFAULT_PAGE_SIZE,
          page: pageIndex
        })
      });
    },
    [sortingObj, pageIndex]
  );

  const searchBySAP = useCallback(
    async (storeId, resetPagination = false) => {
      setSelectedStoreId(storeId);
      if (resetPagination) {
        setPageIndex(0);
      }

      setErrorMessage('');

      try {
        const activityResponse = await getActivityHistory(
          getQueryParams(storeId, true)
        );

        // Check if response has nested data structure (data.data) or direct data array
        const responseData =
          activityResponse?.data?.data || activityResponse?.data;
        const isInvalidOrEmptyResponse =
          activityResponse?.success !== true ||
          !responseData ||
          responseData?.length === 0;
        if (isInvalidOrEmptyResponse) {
          setTableData([]);
          setTotalRecords(0);
          if (showToast) {
            showToast(
              'Data not found. Please retry with a valid SAP ID.',
              'error'
            );
          }
          return;
        }

        const transformedData = transformApiData(responseData);
        setTableData(transformedData);
        setTotalRecords(
          activityResponse?.data?.total_items || transformedData.length
        );
      } catch (error) {
        logger.error('getActivityHistory failed:', error);
        setTableData([]);
        setTotalRecords(0);
        setErrorMessage(
          'An error occurred while fetching activity history. Please try again.'
        );
        if (showToast) {
          showToast(
            'Data not found. Please retry with a valid SAP ID.',
            'error'
          );
        }
      }
    },
    [getQueryParams, transformApiData, showToast, setPageIndex]
  );

  const searchByPhoneNumberHandler = useCallback(
    async (phone) => {
      try {
        const response = await searchPhoneNumber({phone});
        const isEmptyResponse =
          !response?.success || !response?.data || response?.data?.length === 0;
        if (isEmptyResponse) {
          if (showToast) {
            showToast(
              'Data not found. Please retry with a valid phone number.',
              'error'
            );
          }
          return;
        }

        if (response?.data && response?.data?.length > 1) {
          setStoresList(response?.data);
          setShowStores(true);
        } else if (response?.data && response?.data?.length === 1) {
          const storeId = response.data[0]?.storeId;
          await searchBySAP(storeId);
        }
      } catch (error) {
        logger.error('searchPhoneNumber failed:', error);
        if (showToast) {
          showToast(
            'Data not found. Please retry with a valid phone number.',
            'error'
          );
        }
      }
    },
    [searchBySAP, showToast]
  );

  useEffect(() => {
    setSortingObj(DEFAULT_SORT);
  }, [setSortingObj]);

  useEffect(() => {
    if (selectedStoreId) {
      searchBySAP(selectedStoreId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortingObj, selectedStoreId, pageIndex]);

  const handleSearchByChange = useCallback((event) => {
    setSearchBy(event.target.value);
    setErrorMessage('');
  }, []);

  const handleSearchInputChange = useCallback((event) => {
    setSearchInput(event.target.value);
    setErrorMessage('');
  }, []);

  const handleSearch = useCallback(async () => {
    const searchTerm = searchInput.trim();

    if (!searchTerm) {
      setErrorMessage('Please enter a phone number or SAP ID');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setTableData([]);
    setTotalRecords(0);
    setShowStores(false);
    setPageIndex(0); // Reset pagination to first page

    try {
      if (searchBy === 'sapid') {
        await searchBySAP(searchTerm, true);
      } else {
        await searchByPhoneNumberHandler(searchTerm);
      }
    } catch (error) {
      logger.error('Search error:', error);
      if (showToast) {
        showToast(
          'An error occurred while searching. Please try again.',
          'error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    searchBy,
    searchBySAP,
    searchByPhoneNumberHandler,
    searchInput,
    setPageIndex,
    showToast
  ]);

  const handleStoreSelect = useCallback(
    async (store) => {
      setIsLoading(true);
      try {
        const {storeId} = store;
        await searchBySAP(storeId, true);
        setShowStores(false);
      } catch (error) {
        logger.error('handleStoreSelect failed:', error);
        setTableData([]);
        setTotalRecords(0);
        setErrorMessage('An error occurred while searching. Please try again.');
        if (showToast) {
          showToast(
            'Data not found. Please retry with valid information.',
            'error'
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [searchBySAP, showToast]
  );

  const handleDownload = useCallback(
    (selectedRows = []) => {
      if (tableData?.length === 0) return;
      const dataToDownload =
        selectedRows && selectedRows.length > 0 ? selectedRows : tableData;

      const csvContent = [
        [
          'Event ID',
          'Activity',
          'Points',
          'Balance Points',
          'Points Type',
          'Description',
          'Creation Date'
        ],
        ...dataToDownload.map((row) => [
          row.id,
          row.activityName,
          row.pointsAwarded,
          row.endpointBalance,
          row.type,
          `"${row.description.replaceAll('"', '""')}"`,
          formatDate(row.createdAt)
        ])
      ]
        .map((row) => row.join(','))
        .join('\n');

      const fileName = `activity_history_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSVFiles(csvContent, fileName);
    },
    [tableData, formatDate]
  );

  const isSearchEnabled = useMemo(() => {
    return searchBy !== '' && searchInput.trim() !== '';
  }, [searchBy, searchInput]);

  const placeholder = useMemo(() => {
    switch (searchBy) {
      case 'phone':
        return 'Enter Phone Number';
      case 'sapid':
        return 'Enter SAP ID';
      default:
        return 'Enter data';
    }
  }, [searchBy]);

  const getPlaceholder = useCallback(() => placeholder, [placeholder]);

  return {
    searchBy,
    searchInput,
    tableData,
    showStores,
    storesList,
    isLoading,
    errorMessage,
    totalRecords,
    isSearchEnabled,
    handleSearchByChange,
    handleSearchInputChange,
    handleSearch,
    handleStoreSelect,
    handleDownload,
    getPlaceholder,
    setSortingObj,
    sortingObj,
    setPageIndex,
    pageIndex,
    searchBySAP
  };
};
